/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import SessionStatusCards from '@/components/feedback/SessionStatusCards';
import TimeSeriesPreview from '@/components/charts/TimeSeriesPreview';

/* Import types */
import type { TabularOutputState } from '@/types/ui';

interface LoginTabProps {
    accessToken: string
    hasSession: boolean
    isCheckingSession: boolean
    outputState: TabularOutputState
    onCheckSession: () => void
    onClearSession: () => void
    requestStatus: string
    sessionImportedAt: string
    sessionStatus: string
}

function LoginTab({
    accessToken,
    hasSession,
    isCheckingSession,
    onCheckSession,
    onClearSession,
    outputState,
    requestStatus,
    sessionImportedAt,
    sessionStatus,
}: LoginTabProps) {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6">
            <SessionStatusCards
                accessToken={accessToken}
                requestStatus={requestStatus}
                sessionImportedAt={sessionImportedAt}
                sessionStatus={sessionStatus}
            />

            <div className="flex flex-wrap gap-2">
                <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={onCheckSession}
                    disabled={!hasSession || isCheckingSession}
                >
                    {isCheckingSession ? t('Checking API token...') : t('Check API token')}
                </button>

                <button
                    className="btn btn-error"
                    type="button"
                    onClick={onClearSession}
                    disabled={!hasSession}
                >
                    {t('Logout')}
                </button>
            </div>

            {outputState.outputMode === 'timeseries' && outputState.timeSeriesPreview ? (
                <TimeSeriesPreview preview={outputState.timeSeriesPreview} />
            ) : (
                <section className="dashboard-panel flex items-stretch justify-start p-5">
                    <div className="flex w-full flex-col gap-2">
                        <label
                            className="text-sm font-semibold"
                            htmlFor="login-response-output"
                        >
                            {t('Check API token output')}
                        </label>
                        <textarea
                            id="login-response-output"
                            className="textarea textarea-bordered response-output w-full"
                            value={outputState.outputMode === 'raw' ? outputState.responseText : ''}
                            readOnly
                            spellCheck={false}
                            placeholder={t('The API token check response will be shown here.')}
                        />
                    </div>
                </section>
            )}
        </div>
    );
}

export default LoginTab;
